import BaseImage from "@components/BaseImage";
import UTMLink from "@components/UTMLink";
import { GO_TO_BRIDGE } from "@constants/analytics";
import { getTransporterLink } from "@constants/bridgeRoutes";
import { BRIDGE_AGETH_OPTIONS, BRIDGE_OPTIONS } from "@constants/common";
import {
  Button,
  Card,
  Input,
  Typography,
} from "@kelp-dao/kernel-design-system";
import { formatNumber, trackEvent } from "utils";
import { TChainSelection } from "./types";
import { Spinner } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatUnits, type Address } from "viem";
import { useAccount, useBalance, useSwitchChain } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import BridgeTransactionModal from "@components/BridgeWrapV2/BridgeTransactionModal";
import {
  APPROVE_BRIDGE_CLICK,
  BRIDGE_FAILED,
  BRIDGE_INITIATED,
  BRIDGE_SUCCESSFUL,
  MAX_BRIDGE_CLICK,
} from "@constants/analytics";
import { MAX_FRACTION_DIGITS, WrapBridgeMappings } from "@constants/common";
import { isLayerZeroLeg } from "@constants/bridgeRoutes";
import useOftBridgeQuote from "@hooks/bridge/useOftBridgeQuote";
import useOftBridgeSend from "@hooks/bridge/useOftBridgeSend";
import { OFT_LOCAL_DECIMALS, oftBridgePolicy } from "@config/oftBridgeConfig";
import {
  getOftChainEntry,
  getOftRoute,
  resolveBridgeCtaState,
  roundToDust,
  type OftRoute,
} from "@lib/oftBridge";
import { isAmountInput, safeParseUnits } from "@lib/utils";
import TokenBadge, {
  TOKEN_BADGE_CONTAINER,
} from "@components/BridgeWrapV2/TokenBadge";

/**
 * A chain shown as a read-only field.
 *
 * The hand-off card has no amount input, so without this its chains were bare
 * icon-and-text and the card read as unfinished next to the others. Matching
 * the design system's field metrics — same height, radius and border, on the
 * disabled field's background — gives it the same weight while still reading
 * as something you cannot type into.
 */
const ChainField = ({
  label,
  chainId,
  name,
}: {
  label: string;
  chainId: number;
  name: string;
}) => (
  <div className="flex flex-col gap-1 w-full">
    <Typography variant="xsmall" type="body" className="text-text-secondary">
      {label}
    </Typography>
    <div className="flex items-center gap-2 h-[46px] px-3 rounded-md border border-border-default bg-bg-primary">
      <BaseImage
        src={
          WrapBridgeMappings.ICONS[chainId] ||
          WrapBridgeMappings.ICONS.placeholder
        }
        alt={name}
        width={20}
        height={20}
      />
      <Typography variant="small" type="body" className="text-text-primary">
        {name}
      </Typography>
    </div>
  </div>
);

const TransporterBridgeCard = ({
  isAgETH = false,
  fromSymbol,
  fromChain,
  toChain,
  tokenAmount,
}: {
  isAgETH?: boolean;
  /** Accepted for a uniform leg signature; this card shows no receive field. */
  toSymbol?: string;
  fromSymbol: string;
  fromChain: TChainSelection;
  toChain: TChainSelection;
  tokenAmount?: number | null;
}) => {
  const BRIDGE_CONFIG = isAgETH ? BRIDGE_AGETH_OPTIONS : BRIDGE_OPTIONS;
  const bridgeConfigFrom = BRIDGE_CONFIG.find((v) => v.id === fromChain.id);
  const bridgeConfigTo = BRIDGE_CONFIG.find((v) => v.id === toChain.id);

  // agETH still uses its own per-chain Squid links. rsETH resolves a
  // directional Transporter link, which is null when that leg is closed —
  // this replaces the old per-chain link plus the KSP-561/562/563 special
  // case, which existed only because one link per chain could not express
  // direction.
  const bridgeLink = isAgETH
    ? BRIDGE_AGETH_OPTIONS.find((v) => v.id === toChain.id)?.link ?? null
    : getTransporterLink(fromChain.id, toChain.id);

  const fromName = bridgeConfigFrom?.to || fromChain.title;
  const toName = bridgeConfigTo?.to || toChain.title;

  // Only render a balance the caller actually knows. The intermediate
  // "from Ethereum" leg has none, and Number(null) is 0 — which would state a
  // zero balance the user may well not have.
  const hasKnownBalance = tokenAmount !== null && tokenAmount !== undefined;

  return (
    <Card
      variant="non-bordered"
      className="w-full p-4 md:p-8 flex flex-col gap-5 rounded-2xl"
    >
      {/* No amount field here, unlike the native card: this leg hands off to
          Transporter, and Transporter cannot be deep-linked with an amount —
          every candidate query param was verified against the live app and
          none prefills its field. An input would only be retyped there. */}
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <ChainField label="From" chainId={fromChain.id} name={fromName} />
        <ChainField label="To" chainId={toChain.id} name={toName} />
      </div>
      {hasKnownBalance && (
        <div className="flex justify-between gap-1">
          <Typography
            variant="small"
            type="body"
            className="text-text-secondary"
          >
            Your {fromSymbol} on {fromName}
          </Typography>
          <Typography variant="small" type="body" className="text-text-primary">
            {formatNumber(
              Number(tokenAmount),
              false,
              Number(tokenAmount) < 0.0001 ? 6 : 4,
              true
            )}{" "}
            {fromSymbol}
          </Typography>
        </div>
      )}
      <div className="flex justify-between gap-1">
        <Typography variant="small" type="body" className="text-text-secondary">
          Est. time to bridge
        </Typography>
        <Typography variant="small" type="body" className="text-text-primary">
          ~10 Mins
        </Typography>
      </div>
      {bridgeLink && (
        <UTMLink
          href={bridgeLink}
          isExternal={true}
          onClick={() => {
            trackEvent(GO_TO_BRIDGE, {
              source_chain: fromChain.title,
              destination_chain: toChain.title,
              from_chain_id: String(fromChain.id),
              to_chain_id: String(toChain.id),
            });
          }}
        >
          <Button
            variant="primary"
            icon={
              <BaseImage
                src={bridgeConfigFrom?.icon || ""}
                alt={bridgeConfigFrom?.to || ""}
                width={18}
                height={18}
              />
            }
            className="w-full"
          >
            Go to Bridge
          </Button>
        </UTMLink>
      )}
      {bridgeLink && !isAgETH && (
        <Typography
          variant="xsmall"
          type="body"
          className="text-text-secondary text-center"
        >
          You&apos;ll enter the amount on Transporter, Chainlink&apos;s CCIP
          bridge.
        </Typography>
      )}
    </Card>
  );
};

/**
 * Renders one leg of a bridge journey.
 *
 * Arbitrum is bridged natively in-app against the LayerZero OFT contracts;
 * every other chain links out to Transporter. The agETH journey shares this
 * component and has its own Ethereum <-> Arbitrum route on a different asset,
 * so the native path is gated on `isAgETH` as well as the leg — resolving on
 * chain ids alone would show an rsETH widget in the agETH flow and bridge the
 * wrong asset.
 */
const BridgeAccordionV2 = (props: {
  isAgETH?: boolean;
  toSymbol: string;
  fromSymbol: string;
  fromChain: TChainSelection;
  toChain: TChainSelection;
  tokenAmount?: number | null;
}) => {
  const { isAgETH = false, fromChain, toChain } = props;

  const nativeRoute =
    !isAgETH && isLayerZeroLeg(fromChain.id, toChain.id)
      ? getOftRoute(fromChain.id, toChain.id)
      : null;

  if (nativeRoute) {
    return (
      <NativeBridgeCard
        route={nativeRoute}
        fromChain={fromChain}
        toChain={toChain}
      />
    );
  }

  return <TransporterBridgeCard {...props} />;
};

/* ------------------------------------------------------------------ */
/* Native (LayerZero OFT) leg — Arbitrum only                          */
/* ------------------------------------------------------------------ */

const NativeBridgeCard = ({
  route,
  fromChain,
  toChain,
}: {
  route: OftRoute;
  fromChain: TChainSelection;
  toChain: TChainSelection;
}) => {
  const { address, isConnected, chain: connectedChain } = useAccount();
  const { switchChain } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const [amount, setAmount] = useState("");

  // Guaranteed present — getOftRoute only resolves for chains in the mesh — but
  // resolved as a value to keep this free of non-null assertions.
  const sourceEntry = getOftChainEntry(route.sourceChainId);
  const destinationEntry = getOftChainEntry(route.destinationChainId);

  const { data: rsEthBalance, refetch: refetchRsEth } = useBalance({
    address,
    token: route.tokenAddress,
    chainId: route.sourceChainId,
    query: {
      enabled: Boolean(address),
      refetchInterval: oftBridgePolicy.balanceRefetchMs,
    },
  });

  const { data: nativeBalance } = useBalance({
    address,
    chainId: route.sourceChainId,
    query: {
      enabled: Boolean(address),
      refetchInterval: oftBridgePolicy.balanceRefetchMs,
    },
  });

  const { data: destinationRsEthBalance } = useBalance({
    address,
    token: destinationEntry?.token,
    chainId: route.destinationChainId,
    query: {
      enabled: Boolean(address) && Boolean(destinationEntry),
      refetchInterval: oftBridgePolicy.balanceRefetchMs,
    },
  });

  const typedAmountLD = useMemo(
    () => safeParseUnits(amount, OFT_LOCAL_DECIMALS),
    [amount]
  );

  const roundedAmountLD = useMemo(
    () => roundToDust(typedAmountLD),
    [typedAmountLD]
  );

  const quote = useOftBridgeQuote({
    route,
    amountLD: roundedAmountLD,
    recipient: address,
  });

  const send = useOftBridgeSend({
    route,
    onSuccess: () => {
      void refetchRsEth();
    },
  });

  // Captured at the moment the user clicks the "ready" CTA and never
  // recomputed — the allowance refetches every 15s, and a live recomputation
  // here would make the Approve step vanish mid-flow the instant it lands.
  const needsApprovalSnapshot = useRef(false);

  // The account connected when needsApprovalSnapshot was taken. If the wallet
  // switches accounts while the modal is open, that snapshot describes an
  // allowance that no longer belongs to the connected address — submit() checks
  // this against the live address and refuses rather than silently skipping a
  // needed approval.
  const accountSnapshot = useRef<Address | undefined>(undefined);

  // The quote re-polls every 30s — including throughout the wallet-confirmation
  // window — and can go null (e.g. the wallet account changes, which re-keys the
  // quote query since `recipient` is part of its key). Everything downstream of
  // the "ready" click (the modal, the submit call, the success analytics) must
  // read this snapshot instead of the live quote, or a null quote silently turns
  // into a zero fee and the send reverts on-chain after the user already signed.
  const nativeFeeSnapshot = useRef<bigint | null>(null);

  // Guards the Confirm button against firing BRIDGE_INITIATED / calling submit
  // more than once per flow (e.g. a double click before the "review" step's
  // state has visibly changed). Reset whenever a new flow starts.
  const hasInitiatedSubmitRef = useRef(false);

  const analyticsPayload = {
    source_chain: fromChain.title,
    destination_chain: toChain.title,
    from_chain_id: String(fromChain.id),
    to_chain_id: String(toChain.id),
    amount,
    dapp: "kelp",
  };
  // Kept fresh every render so the step-transition effect below can read the
  // latest payload without needing it in its dependency array — the effect must
  // only re-run when `send.step` itself transitions, never on incidental re-renders.
  const analyticsPayloadRef = useRef(analyticsPayload);
  analyticsPayloadRef.current = analyticsPayload;

  useEffect(() => {
    if (send.step.kind === "approving") {
      trackEvent(APPROVE_BRIDGE_CLICK, analyticsPayloadRef.current);
    }
    if (send.step.kind === "done") {
      trackEvent(BRIDGE_SUCCESSFUL, {
        ...analyticsPayloadRef.current,
        hash: send.step.receipt.transactionHash,
        // The fee actually paid, not a re-quote — the live quote has already
        // re-polled one or more times by the time the receipt lands.
        native_fee: String(nativeFeeSnapshot.current ?? 0n),
      });
    }
    // User rejections are not failures — do not report them.
    if (send.step.kind === "error" && !send.step.error.hasUserDenied) {
      trackEvent(BRIDGE_FAILED, {
        ...analyticsPayloadRef.current,
        error_reason: send.step.error.promptError,
      });
    }
  }, [send.step]);

  // `undefined` while the query is loading or has errored — never collapsed to
  // 0n here, since 0n is a real balance and must stay distinguishable from
  // "not known yet".
  const balanceLD = rsEthBalance?.value;
  const nativeSymbol = sourceEntry?.chain.nativeCurrency.symbol ?? "ETH";
  // Widen to 6 decimals below 0.0001 so small holdings don't read as "0".
  const balanceAmount =
    balanceLD === undefined
      ? null
      : Number(formatUnits(balanceLD, OFT_LOCAL_DECIMALS));
  const balanceFractionDigits =
    balanceAmount !== null && balanceAmount < 0.0001 ? 6 : 4;

  const destinationBalanceLD = destinationRsEthBalance?.value;
  // Widen to 6 decimals below 0.0001 so small holdings don't read as "0".
  const destinationBalanceAmount =
    destinationBalanceLD === undefined
      ? null
      : Number(formatUnits(destinationBalanceLD, OFT_LOCAL_DECIMALS));
  const destinationBalanceFractionDigits =
    destinationBalanceAmount !== null && destinationBalanceAmount < 0.0001
      ? 6
      : 4;

  const cta = resolveBridgeCtaState({
    isConnected,
    connectedChainId: connectedChain?.id,
    sourceChainId: route.sourceChainId,
    sourceChainName: fromChain.title,
    nativeSymbol,
    typedAmountLD,
    roundedAmountLD,
    balanceLD,
    nativeBalance: nativeBalance?.value,
    quoteFee: quote.nativeFee,
    isQuoteLoading: quote.isLoading,
    isQuoteError: quote.isError,
  });

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (!isAmountInput(val) && val !== "") return;
    if (val.indexOf(".") === -1) {
      setAmount(val);
      return;
    }
    if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
      setAmount(val);
    }
  };

  // MAX uses the dust-rounded balance, further truncated (not rounded — this must
  // never exceed the wallet's actual balance) to the same MAX_FRACTION_DIGITS the
  // input itself enforces. Without this, MAX can write more decimals than
  // handleChange accepts, and the field becomes stuck/uneditable afterwards.
  const handleMax = () => {
    // Never compute MAX from a fabricated zero while the balance is unknown.
    if (balanceLD === undefined) return;
    const dustRounded = formatUnits(roundToDust(balanceLD), OFT_LOCAL_DECIMALS);
    const dotIndex = dustRounded.indexOf(".");
    const maxAmount =
      dotIndex === -1
        ? dustRounded
        : dustRounded.slice(0, dotIndex + 1 + MAX_FRACTION_DIGITS);
    // Report the amount MAX actually wrote, not the pre-click `amount` (usually "").
    trackEvent(MAX_BRIDGE_CLICK, { ...analyticsPayload, amount: maxAmount });
    setAmount(maxAmount);
  };

  const ctaLabel = () => {
    switch (cta.kind) {
      case "connect":
        return "Connect Wallet";
      case "switch":
        return `Switch to ${cta.chainName}`;
      case "enter-amount":
        return "Enter an amount";
      case "balance-loading":
        return "Loading balance…";
      case "over-balance":
        return "Insufficient rsETH balance";
      case "below-dust":
        return "Amount too small";
      case "quote-loading":
        return "Fetching bridge fee";
      case "quote-error":
        return "Retry fee estimate";
      case "insufficient-fee":
        return `Not enough ${cta.symbol} for the bridge fee`;
      case "ready":
        return "Bridge rsETH";
    }
  };

  const handleCtaClick = () => {
    if (cta.kind === "connect") {
      openConnectModal?.();
      return;
    }
    if (cta.kind === "switch") {
      switchChain({ chainId: route.sourceChainId });
      return;
    }
    if (cta.kind === "quote-error") {
      quote.refetch();
    }
    if (cta.kind === "ready") {
      needsApprovalSnapshot.current = !send.hasAllowanceFor(roundedAmountLD);
      // Snapshotted alongside needsApproval — isConnected being true here
      // guarantees `address` is defined.
      accountSnapshot.current = address;
      // Snapshot the fee too — resolveBridgeCtaState only reaches "ready" when
      // quote.nativeFee is non-null, so this is guaranteed non-null here even
      // though the live quote can go null later while the modal is open.
      nativeFeeSnapshot.current = quote.nativeFee;
      hasInitiatedSubmitRef.current = false;
      send.openReview();
    }
  };

  // The LayerZero fee is snapshotted at Confirm so what's submitted always
  // matches what the user reviewed. A retry must not blindly resend that same
  // (possibly stale) snapshot — the quote can have risen since the failed
  // attempt, and reusing an under-priced fee reverts on-chain. Refresh from the
  // live quote immediately before replaying; if no live quote is available yet,
  // do nothing rather than resubmit with a value that was never reviewed.
  const handleRetry = () => {
    const freshFee = quote.nativeFee;
    if (freshFee === null) return;
    nativeFeeSnapshot.current = freshFee;
    send.retry({ nativeFee: freshFee });
  };

  const isActionable =
    cta.kind === "connect" ||
    cta.kind === "switch" ||
    cta.kind === "quote-error" ||
    cta.kind === "ready";

  const feeText =
    quote.nativeFee === null
      ? "—"
      : `${formatNumber(
          Number(
            formatUnits(
              quote.nativeFee,
              sourceEntry?.chain.nativeCurrency.decimals ?? 18
            )
          ),
          false,
          6,
          true
        )} ${nativeSymbol}`;

  return (
    <Card
      variant="non-bordered"
      className="w-full p-4 md:p-8 flex flex-col gap-5 rounded-2xl"
    >
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div className="flex flex-col gap-1 w-full">
          <div className="flex justify-between items-center gap-2">
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary"
            >
              From
            </Typography>
            <div className="flex items-center gap-1">
              <BaseImage
                src={
                  WrapBridgeMappings.ICONS[fromChain.id] ||
                  WrapBridgeMappings.ICONS.placeholder
                }
                alt={fromChain.title}
                width={18}
                height={18}
              />
              <Typography
                variant="xsmall"
                type="body"
                className="text-text-secondary"
              >
                {fromChain.title}
              </Typography>
            </div>
          </div>
          <Input
            value={amount}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Enter amount"
            containerClassName="w-full"
            rightElementClassName={TOKEN_BADGE_CONTAINER}
            rightElement={<TokenBadge symbol="rsETH" />}
          />
          <div className="flex justify-between items-center gap-2 h-[40px]">
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary whitespace-nowrap"
            >
              Balance:{" "}
              <strong>
                {balanceAmount === null
                  ? "—"
                  : formatNumber(
                      balanceAmount,
                      false,
                      balanceFractionDigits,
                      true
                    )}{" "}
                rsETH
              </strong>
            </Typography>
            <Button
              variant="outline"
              size="xsmall"
              disabled={balanceLD === undefined}
              onClick={handleMax}
            >
              MAX
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-1 w-full">
          <div className="flex justify-between items-center gap-2">
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary"
            >
              To
            </Typography>
            <div className="flex items-center gap-1">
              <BaseImage
                src={
                  WrapBridgeMappings.ICONS[toChain.id] ||
                  WrapBridgeMappings.ICONS.placeholder
                }
                alt={toChain.title}
                width={18}
                height={18}
              />
              <Typography
                variant="xsmall"
                type="body"
                className="text-text-secondary"
              >
                {toChain.title}
              </Typography>
            </div>
          </div>
          <Input
            value={amount}
            onChange={() => {}}
            placeholder="0"
            disabled
            containerClassName="w-full"
            // Disabled, but this is the amount the user receives — render it as
            // a value rather than in the placeholder's grey, which read as empty.
            inputClassName="!text-text-primary !opacity-100"
            rightElementClassName={TOKEN_BADGE_CONTAINER}
            rightElement={<TokenBadge symbol="rsETH" />}
          />
          <div className="flex justify-between items-center gap-2 h-[40px]">
            <Typography
              variant="xsmall"
              type="body"
              className="text-text-secondary whitespace-nowrap"
            >
              Balance:{" "}
              <strong>
                {destinationBalanceAmount === null
                  ? "—"
                  : formatNumber(
                      destinationBalanceAmount,
                      false,
                      destinationBalanceFractionDigits,
                      true
                    )}{" "}
                rsETH
              </strong>
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex justify-between gap-1">
        <Typography variant="small" type="body" className="text-text-secondary">
          Bridge fee
        </Typography>
        <Typography variant="small" type="body" className="text-text-primary">
          {feeText}
        </Typography>
      </div>

      <div className="flex justify-between gap-1">
        <Typography variant="small" type="body" className="text-text-secondary">
          Est. time to bridge
        </Typography>
        <Typography variant="small" type="body" className="text-text-primary">
          ~2-3 Mins
        </Typography>
      </div>

      <Button
        variant="primary"
        className="w-full"
        disabled={!isActionable}
        onClick={handleCtaClick}
      >
        {cta.kind === "quote-loading" && <Spinner size="sm" color="white" />}
        {ctaLabel()}
      </Button>

      {send.step.kind !== "idle" && sourceEntry && destinationEntry && (
        <BridgeTransactionModal
          isOpen
          sourceChain={sourceEntry.chain}
          destinationChain={destinationEntry.chain}
          sourceChainName={fromChain.title}
          destinationChainName={toChain.title}
          amountLD={roundedAmountLD}
          nativeFee={nativeFeeSnapshot.current ?? 0n}
          needsApproval={needsApprovalSnapshot.current}
          // Replayable at the hook level AND a live fee is available to refresh
          // with — handleRetry no-ops without one, so the button must not
          // pretend it can retry when it can't.
          canRetry={send.canRetry && quote.nativeFee !== null}
          step={send.step}
          onConfirm={() => {
            const nativeFee = nativeFeeSnapshot.current;
            // Never broadcast a send with a substituted zero fee, and never
            // double-fire BRIDGE_INITIATED / double-submit on a fast double click.
            if (nativeFee === null || hasInitiatedSubmitRef.current) return;
            hasInitiatedSubmitRef.current = true;
            trackEvent(BRIDGE_INITIATED, analyticsPayload);
            void send.submit({
              amountLD: roundedAmountLD,
              nativeFee,
              expectedAddress: accountSnapshot.current,
            });
          }}
          onRetry={handleRetry}
          onClose={() => {
            if (send.step.kind === "done") setAmount("");
            send.close();
          }}
        />
      )}
    </Card>
  );
};

export default BridgeAccordionV2;
